import { CloseIcon, InfoOutlineIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Flex, Text, InputGroup, InputLeftElement, InputRightElement, Input, Spinner, List, ListItem, SkeletonText, Card, CardBody, Tooltip } from '@chakra-ui/react'
import React, { useState, useEffect, useRef } from 'react'
import { FaReact } from 'react-icons/fa';
import { MdBlock } from 'react-icons/md';
import { useGetApplicationsQuery, useGetAppDetailsQuery, useGetAdvertisableAppsQuery } from 'store/campaigns/adsetFormApi';

function AppNameStoreAdspresso({adsAccountId, isSaving, adSetFormik, onAppChange}) {

    const inputRef = useRef(null);
    const popupRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [appOptions, setAppOptions] = useState([]);
    const [selectedAppOptions, setSelectedAppOptions] = useState({});
    const [selectedAppId, setSelectedAppId] = useState(''); // Track selected app ID from advertisableApps
    const [isUserInteracted, setIsUserInteracted] = useState(false); // Track if user has interacted
    const hasInitialized = useRef(false); // Track initialization

    const matchingPromotedObject = adSetFormik?.promoted_object?.find(
        (obj) => obj.adsAccountId === adsAccountId
    );
      
    // Only use matchingPromotedObject URL if user hasn't interacted yet, or use selected app URL
    const objectUrl = selectedAppOptions?.url || (!isUserInteracted ? matchingPromotedObject?.object_store_url : null);

    // Add advertisable apps query
    const { data: advertisableApps } = useGetAdvertisableAppsQuery(
        { adsAccountId },
        { skip: !adsAccountId }
    );

    const { data: matchedApplications, isLoading: isMatchedAppLoading, isFetching, refetch } = useGetApplicationsQuery(
        { searchTerm: selectedAppId || debouncedSearchTerm, adsAccountId, app_store: adSetFormik?.app_store }, 
        { skip: !selectedAppId && !debouncedSearchTerm?.length }
    );
    
    const { data: appDetails, isLoading:isAppDetailLoading } = useGetAppDetailsQuery(
        { object_url: objectUrl, adsAccountId },
        { skip: !objectUrl }
    );

    // Initialize from promoted_object on mount
    useEffect(() => {
        if (!hasInitialized.current && matchingPromotedObject?.application_id && advertisableApps?.length > 0 && !isUserInteracted) {
            hasInitialized.current = true;
            
            // Find the app in advertisableApps
            const existingApp = advertisableApps.find(
                app => app.id === matchingPromotedObject.application_id
            );
            
            if (existingApp) {
                // Set selectedAppId to trigger the matchedApplications query
                setSelectedAppId(existingApp.id);
            }
        }
    }, [matchingPromotedObject, advertisableApps, isUserInteracted]);

    // Auto-select when matchedApplications updates after selecting from advertisableApps
    useEffect(() => {
        if (selectedAppId && matchedApplications?.length > 0) {
            const app = matchedApplications[0];
            
            setSelectedAppOptions({
                name: app.name,
                url: app.url || app.original_object_url,
            });

            // Don't call onAppChange here, let appDetails effect handle it
            setSelectedAppId('');
            setShowPopup(false);
            setAppOptions([]);
        }
    }, [matchedApplications, selectedAppId]);

    // Handle app details response - this will have the ID
    useEffect(() => {
        if (appDetails?.data?.length > 0) {
            const appData = appDetails.data[0];
            
            onAppChange([
                {
                    id: appData.id,
                    original_object_url: appData.original_object_url,
                }
            ]);
            
            // Always update selectedAppOptions when appDetails loads
            setSelectedAppOptions({
                name: appData.name,
                url: appData.original_object_url
            });
        }
    }, [appDetails]);

    // Clear old results when user starts typing new search
    useEffect(() => {
        if (searchTerm?.length > 0) {
            // User is typing, clear old results and selected app immediately
            setAppOptions([]);
            setShowPopup(false);
            setSelectedAppOptions({});
            onAppChange([]);
            setIsUserInteracted(true); // Mark that user has interacted
        }
        
        if (searchTerm?.length == 0) {
            setDebouncedSearchTerm('');
            setAppOptions([]);
            setShowPopup(false);
            return;
        }

        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 800);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (searchTerm?.length > 0 && (searchTerm == debouncedSearchTerm)) {
            refetch()
        }
    }, [searchTerm, debouncedSearchTerm])

    useEffect(() => {
        // Only show popup for manual search, not for advertisableApps selection
        // Also don't show popup if an app is already selected
        if (matchedApplications?.length > 0 && !selectedAppId && !selectedAppOptions?.name) {
            setShowPopup(true);
            setAppOptions(matchedApplications);
        } else if (debouncedSearchTerm && !selectedAppId && matchedApplications?.length === 0) {
            // Clear and hide if we were searching and got no results
            setAppOptions([]);
            setShowPopup(false);
        }
    }, [matchedApplications, selectedAppId, debouncedSearchTerm, selectedAppOptions?.name]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                popupRef.current &&
                !popupRef.current.contains(event.target)
            ) {
                setShowPopup(false);
                setAppOptions([]);
            }

        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const handleAppSelect = (app) => {
        setIsUserInteracted(true); // Mark that user has interacted

        // Check if this is from advertisableApps (has id property but no app_id or unique_id)
        if (app.id && !app.app_id && !app.unique_id) {
            // This is from advertisableApps, need to fetch full details via matchedApplications
            setSelectedAppId(app.id);
            setSelectedAppOptions({
                name: app.name,
                url: app.original_object_url
            });
        } else {
            // This is from search results (matchedApplications), need to fetch details via appDetails
            setSelectedAppId('');
            
            setSelectedAppOptions({
                name: app.name,
                url: app.url || app.original_object_url,
            });
            
            // The appDetails query will automatically trigger because objectUrl changed
            // and the appDetails useEffect will call onAppChange with the ID
        }

        // Close popup and clear search after setting selected app
        setShowPopup(false);
        setAppOptions([]);
        setSearchTerm('');
        setDebouncedSearchTerm('');
    };

    const handleAppStoreRedirect = () => {
        if (selectedAppOptions?.url) {
            window.open(selectedAppOptions?.url, '_blank');
        }
    };

    const handleInputFocus = () => {
        const appStore = adSetFormik?.app_store;

        // Don't open if already selected
        if (selectedAppOptions?.name) return;

        // If there's already a search in progress, don't override
        if (appOptions?.length > 0) {
            setShowPopup(true);
            return;
        }

        // Show default advertisable apps when no search term
        if (!searchTerm?.trim() && advertisableApps && advertisableApps.length > 0 && appStore) {
            const storeFiltered = advertisableApps.filter((app) => {
                if (appStore === 'google_play') {
                    return app.cpa_access?.google_play === false;
                } else if (appStore === 'itunes') {
                    return app.cpa_access?.itunes === false;
                }
                return false;
            });

            setAppOptions(storeFiltered);
            setShowPopup(storeFiltered.length > 0);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // If user starts typing and there's a selected app, clear it
        if (value && selectedAppOptions?.name) {
            setSelectedAppOptions({});
            onAppChange([]);
            setIsUserInteracted(true); // Mark that user has interacted
        }
    };

    return (
        <Flex direction="column" gap={1} w={'100%'}>
            <Flex align={'center'}>
                <Text fontWeight="bold">App name</Text>
                <Box>
                    <Tooltip
                        borderRadius={5}
                        label={
                            <Box fontSize="12px" p={'5px'}>
                                <Text>
                                    You can advertise any app registered on Meta's developer site. 
                                </Text>
                            </Box>
                        }
                        fontSize="md"
                    >
                        <InfoOutlineIcon pointerEvents="all" ml={2} />
                    </Tooltip>
                </Box>
            </Flex>
            {isAppDetailLoading ? 
                <Flex>
                    <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                </Flex>
            :
            <Box position="relative">
                <Text fontSize={'12px'}>Choose the app you want to advertise.</Text>
                <Flex>
                    <InputGroup >
                        <InputLeftElement height={'32px'}>
                            <SearchIcon boxSize={4} color='gray.500' />
                        </InputLeftElement>
                        <Input
                            ref={inputRef}
                            value={selectedAppOptions?.name ? selectedAppOptions?.name : searchTerm}
                            onChange={handleInputChange}
                            placeholder="Enter app name, app ID or exact app store URL"
                            borderRadius={'md'}
                            size={'sm'}
                            onFocus={handleInputFocus}
                        />
                        <InputRightElement height={'32px'} cursor={'pointer'}>
                            {isFetching ? (
                                <Spinner boxSize={3} />
                            ) : (<>
                                {searchTerm?.length > 0 || selectedAppOptions?.name ? (
                                    <CloseIcon boxSize={3} color='gray.500' onClick={() => { 
                                        setSearchTerm(''); 
                                        setDebouncedSearchTerm(''); 
                                        setSelectedAppOptions({}); 
                                        setSelectedAppId('');
                                        setAppOptions([]);
                                        setShowPopup(false);
                                        setIsUserInteracted(true); // Mark interaction
                                        onAppChange([]); 
                                    }} />
                                ) : null}
                            </>)}
                        </InputRightElement>
                    </InputGroup>
                </Flex>
                <Flex mt={2}>
                    {showPopup && appOptions.length > 0 && (
                        <List
                            ref={popupRef}
                            position="absolute"
                            top="100%"
                            width="100%"
                            border="1px solid #ccc"
                            backgroundColor="white"
                            zIndex="1000"
                            maxHeight="200px"
                            overflowY="auto"
                        >
                            {appOptions.map((app) => (
                                <ListItem
                                    key={app.unique_id ? (app.unique_id * Math.random() + app.name) : app.id}
                                    p={'5px'}
                                    px={'10px'}
                                    cursor="pointer"
                                    _hover={{ backgroundColor: '#f0f0f0' }}
                                    onClick={() => handleAppSelect(app)}
                                >
                                    <Flex gap={2} alignItems={'center'}>
                                        <Box boxSize="24px" display="flex" alignItems="center" justifyContent="center">
                                            {app?.icon_url ? (
                                                <img src={app.icon_url} alt={app.name} style={{ width: '100%', height: '100%', borderRadius: '4px' }} />
                                            ) : (
                                                <Box bg={'gray'} borderRadius={'sm'} w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
                                                    <FaReact size={20} color="#fff" />
                                                </Box>
                                            )}
                                        </Box>
                                        <Flex direction={'column'}>
                                            <Text fontSize={'12px'}>
                                                {app.name}
                                            </Text>
                                            {app.search_source_store === 'own_apps' ? 
                                                <Text fontSize={'10px'}>
                                                    {`App ID : ${app.app_id}`}
                                                </Text>
                                            : app.app_id ?
                                                <Text fontSize={'10px'}>
                                                    {`App ID : ${app.app_id}`}
                                                </Text>
                                            : app.id ?
                                                <Text fontSize={'10px'}>
                                                    {`App ID : ${app.id}`}
                                                </Text>
                                            :
                                                <Text fontSize={'10px'}>
                                                    Not connected to your ad account
                                                </Text>
                                            }
                                        </Flex>
                                    </Flex>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Flex>

                {!showPopup && !selectedAppOptions?.name ?
                    <Card 
                        bg={isSaving ? '#fff' : 'rgb(224,237,247)'} 
                        borderRadius="5px" 
                        p={2}
                        borderLeft={isSaving ? "4px solid rgb(220,55,60)" : null}
                    >
                        <CardBody display="flex" alignItems="center" gap={2} p={0}>
                            {isSaving ? (
                                <MdBlock style={{ pointerEvents: 'all'}} color="rgb(220,55,60)" />
                            ) : (
                                <InfoOutlineIcon style={{ pointerEvents: 'all', marginLeft:'4px'}} color="rgb(9,120,190)" />
                            )}
                        <Text fontSize="12px" fontWeight="bold">
                            Select the app that you want people to install and use.
                        </Text>
                        </CardBody>
                    </Card>
                  
                : 
                    <Text
                        _hover={{ textDecoration: 'underline', cursor: 'pointer' }}
                        fontWeight={'normal'}
                        color={'inherit'}
                        fontSize={'10px'}
                        mt={-1}
                        onClick={handleAppStoreRedirect}
                    >
                        View this app in your selected app store
                    </Text>
                }
            </Box>
            }
        </Flex>
    )
}

export default AppNameStoreAdspresso;